/**
 * Inversiones (Finanzas · bloque I, decidido con Alex el 11 oct 2026).
 *
 *  - Una sola lista a fecha de hoy: cuánto lleva cada inversión y cuánto le
 *    queda por desgastar (`investmentProgress`).
 *  - El tipo se adivina por el nombre y sugiere los años (`adivinarTipo`).
 *  - Aviso si la vida útil se sale del rango del tipo en más de un 50 %
 *    (`avisoVidaUtil`).
 *  - Gastos que parecen inversiones, por lo que son (`gastosQueParecenInversiones`).
 *  - Inversiones «de todos los locales», repartidas como los gastos comunes
 *    (`allocateSharedInvestments`).
 *
 * Desgaste = depreciación lineal sin IVA: costo ÷ (años × 12) cada mes, desde
 * el mes de compra (mes completo) hasta cumplir la vida útil o hasta «Ya no
 * lo tengo». La cuenta la hace `depreciationForMonth` (finance.ts).
 */
import { depreciationForMonth, investmentAmount, monthlyDepreciation, revenueOf } from './finance';
import { USEFUL_LIFE_ITEMS, suggestUsefulLife, type UsefulLifeItem } from './investmentGuide';
import type { Loan } from './loans';
import { addMonths, monthDiff, monthOf } from './period';
import {
  effectiveLocationId,
  SHARED_LOCATION_ID,
  type DailyRevenue,
  type ExpenseEntry,
  type Investment,
  type ParteDelLocal,
  type SharedCostAllocation,
} from './types';
import { round } from './units';

/* ------------------------------------------------------------------ */
/* Tipo y años sugeridos                                                */
/* ------------------------------------------------------------------ */

/** Categorías de la guía que en un restaurante son equipo profesional. */
const CATEGORIAS_PROFESIONALES = new Set([
  'Cocina y preparación',
  'Área de servicio',
  'Área de limpieza',
  'Instalaciones generales',
]);

export type TipoInversion = UsefulLifeItem & { category: string };

/**
 * Años sugeridos (regla 2): el número alto del rango si es equipo
 * profesional (cocina, servicio, instalaciones), el bajo si no se sabe.
 */
export function aniosSugeridos(tipo: TipoInversion): number {
  return CATEGORIAS_PROFESIONALES.has(tipo.category) ? tipo.maxYears : tipo.minYears;
}

/** «Horno mixto» → horno combinado (8–10 años). */
export function adivinarTipo(concepto: string): TipoInversion | undefined {
  return suggestUsefulLife(concepto);
}

export function tipoPorNombre(nombre: string | undefined): TipoInversion | undefined {
  if (!nombre) return undefined;
  return USEFUL_LIFE_ITEMS.find((i) => i.name === nombre);
}

/** El tipo guardado, o el que se adivina por el nombre. */
export function tipoDeInversion(inv: Pick<Investment, 'assetType' | 'concept'>): TipoInversion | undefined {
  return tipoPorNombre(inv.assetType) ?? adivinarTipo(inv.concept);
}

export interface AvisoVidaUtil {
  tipo: TipoInversion;
  anios: number;
  /** Lo que se propone con «Cambiar a N años». */
  sugerido: number;
}

/**
 * Regla 3: aviso ámbar si los años se salen del rango del tipo en más de un
 * 50 % (25 años para un horno de 8–10). «Dejarlo así» no vuelve a avisar.
 */
export function avisoVidaUtil(
  inv: Pick<Investment, 'assetType' | 'concept' | 'usefulLifeYears' | 'lifeWarningDismissed'>,
): AvisoVidaUtil | undefined {
  if (inv.lifeWarningDismissed) return undefined;
  const anios = inv.usefulLifeYears;
  if (!anios || anios <= 0) return undefined;
  const tipo = tipoDeInversion(inv);
  if (!tipo) return undefined;
  if (anios > tipo.maxYears * 1.5) return { tipo, anios, sugerido: tipo.maxYears };
  if (anios < tipo.minYears * 0.5) return { tipo, anios, sugerido: tipo.minYears };
  return undefined;
}

/* ------------------------------------------------------------------ */
/* Cuánto lleva cada inversión                                          */
/* ------------------------------------------------------------------ */

export type EstadoInversion = 'futura' | 'desgastando' | 'terminada' | 'ya_no_la_tiene';

export interface ProgresoInversion {
  /** Costo sin IVA. */
  costo: number;
  /** Desgaste de ESTE mes (0 si ya no cuenta). */
  alMes: number;
  /** Desgaste teórico mensual (constante). */
  cuota: number;
  /** Meses que ya contaron (incluido el actual). */
  mesesLlevados: number;
  mesesTotales: number;
  /** Lo que ya se desgastó. */
  desgastado: number;
  /** Lo que le queda por desgastar (0 si terminó o ya no la tiene). */
  leQueda: number;
  estado: EstadoInversion;
  /** Último mes que contó (para «Terminado en mar 2026: ya no cuenta»). */
  terminoEn?: string;
}

/** A fecha de `month` (normalmente el mes de hoy). */
export function investmentProgress(inv: Investment, month: string): ProgresoInversion {
  const costo = investmentAmount(inv);
  const start = monthOf(inv.date);
  const mesesTotales = Math.max(0, (inv.usefulLifeYears || 0) * 12);
  const cuota = monthlyDepreciation(inv);
  const baja = inv.disposedOn ? monthOf(inv.disposedOn) : undefined;

  if (month < start) {
    return { costo, alMes: 0, cuota, mesesLlevados: 0, mesesTotales, desgastado: 0, leQueda: round(costo, 2), estado: 'futura' };
  }
  // Meses que contaron: del de compra al consultado, sin pasar de la vida
  // útil ni llegar al mes de la baja.
  let llevados = monthDiff(start, month) + 1;
  if (baja) llevados = Math.min(llevados, Math.max(0, monthDiff(start, baja)));
  if (mesesTotales > 0) llevados = Math.min(llevados, mesesTotales);
  else llevados = Math.min(llevados, 1); // sin vida útil: todo el mes de compra

  const desgastado = mesesTotales > 0 ? Math.min(costo, cuota * llevados) : llevados > 0 ? costo : 0;
  const alMes = depreciationForMonth(inv, month);
  const terminada = mesesTotales > 0 ? llevados >= mesesTotales : true;
  const yaNoLaTiene = Boolean(baja && month >= baja);
  const estado: EstadoInversion = yaNoLaTiene ? 'ya_no_la_tiene' : terminada ? 'terminada' : 'desgastando';
  const terminoEn = yaNoLaTiene
    ? baja
    : terminada
      ? addMonths(start, Math.max(0, (mesesTotales || 1) - 1))
      : undefined;
  return {
    costo: round(costo, 2),
    alMes: round(alMes, 2),
    cuota: round(cuota, 2),
    mesesLlevados: llevados,
    mesesTotales,
    desgastado: round(desgastado, 2),
    leQueda: estado === 'desgastando' ? round(Math.max(0, costo - desgastado), 2) : 0,
    estado,
    terminoEn,
  };
}

export interface ResumenInversiones {
  /** Te cuestan cada mes (desgaste de este mes). */
  alMes: number;
  /** Invertiste: costo sin IVA de lo que sigues usando. */
  invertido: number;
  /** Cosas que sigues usando. */
  enUso: number;
  /** Les queda por desgastar. */
  leQueda: number;
}

/** Los tres números de arriba de Inversiones. */
export function resumenInversiones(investments: readonly Investment[], month: string): ResumenInversiones {
  let alMes = 0;
  let invertido = 0;
  let enUso = 0;
  let leQueda = 0;
  for (const inv of investments) {
    const p = investmentProgress(inv, month);
    alMes += p.alMes;
    leQueda += p.leQueda;
    if (p.estado === 'desgastando') {
      invertido += p.costo;
      enUso += 1;
    }
  }
  return { alMes: round(alMes, 2), invertido: round(invertido, 2), enUso, leQueda: round(leQueda, 2) };
}

/* ------------------------------------------------------------------ */
/* Gastos que parecen inversiones                                        */
/* ------------------------------------------------------------------ */

function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/** Regla 4: lo que dura años, por lo que es. */
const PALABRAS_INVERSION =
  /\b(horno|hornos|estufa|estufas|refrigerador|refrigeradores|refrigeracion|nevera|congelador|camara frigorifica|camara de frio|campana|campanas|plancha|planchas|freidora|freidoras|mesa|mesas|silla|sillas|taburete|taburetes|sofa|vajilla|vajillas|platos|cuberteria|cubiertos|vasos|copas|cristaleria|obra|obras|reforma|reformas|remodelacion|instalacion|mobiliario|lavavajillas|cafetera|vitrina|licuadora|batidora|procesador|aire acondicionado|computadora|laptop|tablet|terminal punto de venta)\b/;

/** Lo que casi nunca es inversión aunque lleve esas palabras. */
const PALABRAS_NO =
  /\b(desechable|desechables|carton|plastico|servilleta|servilletas|renta|alquiler|mantenimiento|reparacion|reparar|limpieza|gas|recarga|refaccion|repuesto)\b/;

const CATEGORIAS_FUERA = new Set([
  'compras_materia_prima',
  'otros_insumos',
  'bebidas',
  'empaque',
  'limpieza',
  'sueldos_salarios',
  'cuotas_ss',
  'otros_personal',
  'alquiler',
  'seguros',
  'comisiones_bancarias',
  'intereses',
  'impuesto_ganancias',
  'amortizaciones',
  'suministros',
  'suministros_agua',
  'suministros_electricidad',
  'suministros_gas',
  'telefonia',
  'honorarios',
]);

export interface GastoQueParece {
  expense: ExpenseEntry;
  tipo?: TipoInversion;
  /** Ya hay una inversión parecida: probablemente sea una reposición (gasto). */
  reposicion?: Investment;
}

/**
 * Regla 4/7: gastos que parecen inversiones POR LO QUE SON (platos, sillas,
 * horno, campana, obra…), no solo por el monto. Si ya hay una inversión del
 * mismo tipo, se marca como posible reposición (eso es gasto).
 */
export function gastoPareceInversion(
  expense: Pick<ExpenseEntry, 'concept' | 'category' | 'netAmount'>,
): boolean {
  if (CATEGORIAS_FUERA.has(expense.category)) return false;
  return Boolean(pareceInversion(expense.concept)) && expense.netAmount > 0;
}

/**
 * La palabra que delata una inversión en un texto (concepto, nombre del
 * proveedor, fila de un Excel), o undefined. Una sola lista para Gastos
 * (panel, importador, lista) e Inversiones. Solo palabras enteras: «mesa» no
 * salta con «mesero», ni «obra» con «sobrante».
 */
export function pareceInversion(texto: string | undefined): string | undefined {
  const t = normalizar(texto ?? '');
  if (!t.trim() || PALABRAS_NO.test(t)) return undefined;
  return PALABRAS_INVERSION.exec(t)?.[1];
}

export function gastosQueParecenInversiones(
  expenses: readonly ExpenseEntry[],
  investments: readonly Investment[],
  descartados: ReadonlySet<string> = new Set(),
): GastoQueParece[] {
  const yaPasados = new Set(investments.map((i) => i.fromExpenseId).filter(Boolean));
  const tiposExistentes = new Map<string, Investment>();
  for (const inv of investments) {
    const t = tipoDeInversion(inv);
    if (t) tiposExistentes.set(t.name, inv);
  }
  return expenses
    .filter((e) => !descartados.has(e.entryId) && !yaPasados.has(e.entryId) && gastoPareceInversion(e))
    .map((expense) => {
      const tipo = adivinarTipo(expense.concept);
      const reposicion = tipo ? tiposExistentes.get(tipo.name) : undefined;
      return { expense, tipo, ...(reposicion ? { reposicion } : {}) };
    })
    .sort((a, b) => b.expense.date.localeCompare(a.expense.date));
}

/**
 * Regla 5: «Pasar a inversión» con un toque. Los datos de la inversión salen
 * del gasto (fecha, total, IVA, proveedor, local, justificante).
 */
export function inversionDesdeGasto(expense: ExpenseEntry): Omit<Investment, 'tenantId' | 'investmentId'> {
  const tipo = adivinarTipo(expense.concept);
  const iva =
    expense.taxAmount ??
    (expense.invoiceTotal != null ? Math.max(0, expense.invoiceTotal - expense.netAmount) : (expense.netAmount * (expense.vatPct ?? 0)) / 100);
  return {
    date: expense.date,
    concept: expense.concept,
    quantity: 1,
    unitPrice: round(expense.netAmount, 2),
    vatPct: expense.vatPct ?? 0,
    vatAmount: round(iva, 2),
    usefulLifeYears: tipo ? aniosSugeridos(tipo) : 5,
    fundingSource: 'propio',
    ...(tipo ? { assetType: tipo.name } : {}),
    ...(expense.locationId ? { locationId: expense.locationId } : {}),
    ...(expense.supplierId ? { supplierId: expense.supplierId } : {}),
    ...(expense.attachmentKey ? { attachmentKey: expense.attachmentKey } : {}),
    fromExpenseId: expense.entryId,
  };
}

/* ------------------------------------------------------------------ */
/* Varios locales                                                       */
/* ------------------------------------------------------------------ */

/**
 * Parte de un local en lo que es «de todos» (regla 8: se reparte como los
 * gastos compartidos). Con `fijo`, el % de Configuración; por ventas, su
 * parte de las ventas del periodo; sin ventas o sin ajuste, a partes iguales.
 */
export function shareOfLocation(input: {
  locationId: string;
  activeLocationIds: string[];
  allocation?: SharedCostAllocation;
  revenues: readonly DailyRevenue[];
}): number {
  const { locationId, allocation, revenues } = input;
  const active = input.activeLocationIds.length > 0 ? input.activeLocationIds : [locationId];
  if (allocation?.method === 'fijo') {
    const pct = allocation.shares?.[locationId];
    return pct == null ? 0 : pct / 100;
  }
  let own = 0;
  let total = 0;
  for (const day of revenues) {
    const amount = revenueOf(day);
    total += amount;
    if (effectiveLocationId(day.locationId) === locationId) own += amount;
  }
  return total > 0 ? own / total : 1 / active.length;
}

/**
 * Parte de un local en lo «de todos» MES A MES (como `allocateSharedExpenses`):
 * cada mes, su parte de las ventas de ese mes; los meses sin ventas, la del
 * periodo entero (o partes iguales). Con `fijo`, el % de Configuración.
 */
export function parteDelLocalPorMes(input: {
  locationId: string;
  activeLocationIds: string[];
  allocation?: SharedCostAllocation;
  revenues: readonly DailyRevenue[];
}): ParteDelLocal {
  const resto = shareOfLocation(input);
  if (input.allocation?.method === 'fijo') return { porMes: {}, resto };
  const meses = new Map<string, { own: number; total: number }>();
  for (const day of input.revenues) {
    const m = monthOf(day.date);
    const b = meses.get(m) ?? { own: 0, total: 0 };
    const amount = revenueOf(day);
    b.total += amount;
    if (effectiveLocationId(day.locationId) === input.locationId) b.own += amount;
    meses.set(m, b);
  }
  const porMes: Record<string, number> = {};
  for (const [m, b] of meses) if (b.total > 0) porMes[m] = b.own / b.total;
  return { porMes, resto };
}

/** Las inversiones de un local: las suyas enteras + su parte de las «de todos». */
export function allocateSharedInvestments(
  investments: readonly Investment[],
  locationId: string,
  share: number | ParteDelLocal,
): Investment[] {
  const own = investments.filter((i) => effectiveLocationId(i.locationId) === locationId);
  const shared = investments
    .filter((i) => i.locationId === SHARED_LOCATION_ID)
    .map((i) => (typeof share === 'number'
      ? { ...i, locationId, unitPrice: i.unitPrice * share }
      : { ...i, locationId, parteDelLocal: share }));
  return [...own, ...shared];
}

/** Los préstamos de un local: los suyos + su parte de los «de todos» (misma tasa). */
export function allocateSharedLoans(loans: readonly Loan[], locationId: string, share: number | ParteDelLocal): Loan[] {
  const own = loans.filter((l) => effectiveLocationId(l.locationId) === locationId);
  const shared = loans
    .filter((l) => l.locationId === SHARED_LOCATION_ID)
    .map((l) => (typeof share === 'number'
      ? { ...l, locationId, principal: l.principal * share, payment: l.payment * share }
      : { ...l, locationId, parteDelLocal: share }));
  return [...own, ...shared];
}
